/**
 * React-Query bindings for the mail-template callables.
 * Mirror of `functions/src/handlers/mailTemplates.ts`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import type { MailTemplate } from '@cp3/shared';
import { functions } from '../lib/firebase';
import { useUser } from '../auth/AuthProvider';
import { useTargetUidPayload } from './useTargetUidPayload';

/** Shape sent to the upsert callable. We accept a partial / full
 *  template and let the server normalise via Zod defaults. The Zod
 *  schema's input type is too strict here because TypeScript can't
 *  reconcile its defaulted fields with the hook input. */
type MailTemplateUpsert = Partial<MailTemplate> & {
  name: string;
  subject: string;
  blocks: MailTemplate['blocks'];
};

const QK = (uid: string | undefined, targetUid: string | undefined) =>
  ['mail-templates', uid, targetUid ?? null] as const;

export function useMailTemplates() {
  const { user } = useUser();
  const adminPayload = useTargetUidPayload();
  return useQuery({
    queryKey: QK(user?.uid, adminPayload.uid),
    enabled: !!user,
    staleTime: 30_000,
    queryFn: async () => {
      const fn = httpsCallable<{ uid?: string }, { items: MailTemplate[] }>(
        functions,
        'listMailTemplates',
      );
      const r = await fn(adminPayload);
      return r.data.items;
    },
  });
}

export function useMailTemplate(id: string | undefined) {
  const { user } = useUser();
  const adminPayload = useTargetUidPayload();
  return useQuery({
    queryKey: ['mail-template', user?.uid, adminPayload.uid ?? null, id],
    enabled: !!user && !!id,
    queryFn: async () => {
      const fn = httpsCallable<{ id: string; uid?: string }, { template: MailTemplate }>(
        functions,
        'getMailTemplate',
      );
      const r = await fn({ id: id!, ...adminPayload });
      return r.data.template;
    },
  });
}

export function useUpsertMailTemplate() {
  const qc = useQueryClient();
  const { user } = useUser();
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async (input: MailTemplateUpsert) => {
      const fn = httpsCallable<MailTemplateUpsert & { uid?: string }, { id: string }>(
        functions,
        'upsertMailTemplate',
      );
      const r = await fn({ ...input, ...adminPayload });
      return r.data.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK(user?.uid, adminPayload.uid) }),
  });
}

export function useDeleteMailTemplate() {
  const qc = useQueryClient();
  const { user } = useUser();
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async (id: string) => {
      const fn = httpsCallable<{ id: string; uid?: string }, { ok: true }>(
        functions,
        'deleteMailTemplate',
      );
      await fn({ id, ...adminPayload });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK(user?.uid, adminPayload.uid) }),
  });
}

export function useSeedMailTemplates() {
  const qc = useQueryClient();
  const { user } = useUser();
  const adminPayload = useTargetUidPayload();
  return useMutation({
    mutationFn: async () => {
      const fn = httpsCallable<
        { uid?: string },
        { ok: true; written?: number; skipped?: boolean }
      >(functions, 'seedDefaultMailTemplates');
      const r = await fn(adminPayload);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK(user?.uid, adminPayload.uid) }),
  });
}
